import { computed, inject, provide, ref } from 'vue';
import { useMediaQuery } from '@vueuse/core';

// Constantes
const SIDEBAR_COOKIE_NAME = 'sidebar_state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = '16rem';
const SIDEBAR_WIDTH_MOBILE = '17rem';
const SIDEBAR_WIDTH_ICON = '3rem';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

// Symbol pour injection/fournir le context
const SIDEBAR_CONTEXT = Symbol('sidebar-context');

// Hook pour créer le provider
export function createSidebarContext(options = {}) {
  const {
    defaultOpen = true,
    open: openProp,
    onOpenChange: setOpenProp,
  } = options;

  const isMobile = useMediaQuery('(max-width: 767px)');
  const openMobile = ref(false);

  // État interne du sidebar
  const internalOpen = ref(defaultOpen);
  const open = computed(() => openProp ?? internalOpen.value);

  const setOpen = value => {
    const openState = typeof value === 'function' ? value(open.value) : value;
    if (setOpenProp) {
      setOpenProp(openState);
    } else {
      internalOpen.value = openState;
    }

    // Enregistrer dans un cookie
    if (typeof document !== 'undefined') {
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    }
  };

  // Toggle du sidebar
  const toggleSidebar = () => {
    if (isMobile.value) {
      openMobile.value = !openMobile.value;
    } else {
      setOpen(!open.value);
    }
  };

  // État: expanded ou collapsed
  const state = computed(() => (open.value ? 'expanded' : 'collapsed'));

  const context = {
    state,
    open,
    setOpen,
    isMobile,
    openMobile,
    setOpenMobile: value => {
      openMobile.value = value;
    },
    toggleSidebar,
  };

  provide(SIDEBAR_CONTEXT, context);

  return context;
}

// Hook pour utiliser le contexte
export function useSidebar() {
  const context = inject(SIDEBAR_CONTEXT);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

export {
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_MOBILE,
  SIDEBAR_WIDTH_ICON,
  SIDEBAR_KEYBOARD_SHORTCUT,
};
